import { handleDrawingLinkCopy } from "@/features/drawings/utils";
import { HistoryCreatedInfo } from "@/features/history/HistoryCreatedInfo";
import {
  type DesignPlannerResponse,
  type History,
  type NormalizedDrawing,
} from "@repo/api";
import {
  Button,
  ListView,
  ListViewItem,
  Tab,
  TabPanel,
  Tabs,
  Text,
} from "@repo/skapa";
import { t } from "@repo/translation";
import {
  ElementsContainer,
  ModalSheet,
  PaddedContainer,
  useToast,
} from "@repo/ui";
import { hasItems, isDefined } from "@repo/utils";
import { useState } from "react";

import { DrawingArticles } from "./DrawingArticles";
import { DrawingDetails } from "./DrawingDetails";

interface ManageDrawingModalProps {
  isOpen: boolean;
  closeModal: () => void;
  drawing: NormalizedDrawing;
  onDisassociate?: () => void;
  disableAction?: boolean;
  historyEntries?: History[];
  loadPlannerList?: DesignPlannerResponse;
}

type Step = "MANAGE" | "MORE_ACTIONS" | "CONFIRM";

export const ManageDrawingModal = ({
  isOpen,
  closeModal,
  drawing,
  onDisassociate,
  disableAction,
  historyEntries,
}: ManageDrawingModalProps) => {
  const { updateToast } = useToast();

  const [step, setStep] = useState<Step>("MANAGE");

  return (
    <ModalSheet
      title={t("label.planner_drawing")}
      visible={isOpen}
      onClose={closeModal}
      onBack={
        step === "MANAGE"
          ? undefined
          : () => setStep(step === "CONFIRM" ? "MORE_ACTIONS" : "MANAGE")
      }
      footer={
        step === "MANAGE" ? (
          <>
            {drawing.editUrl ? (
              <Button
                text={t("action.edit")}
                href={drawing.editUrl}
                newWindow
                type="primary"
              />
            ) : null}
            <Button
              text={t("label.more_actions")}
              onClick={() => setStep("MORE_ACTIONS")}
            />
          </>
        ) : null
      }
    >
      {step === "MANAGE" && (
        <Tabs
          tabs={[
            hasItems(historyEntries) ? (
              <Tab
                key="drawing-histories-tab"
                text={t("label.history")}
                tabPanelId="drawing-histories"
              />
            ) : null,
            <Tab
              key="drawing-details-tab"
              text={t("label.details")}
              tabPanelId="drawing-details"
            />,
            <Tab
              key="drawing-items-tab"
              text={t("label.articles")}
              tabPanelId="drawing-items"
            />,
          ].filter(isDefined)}
          tabPanels={[
            <TabPanel
              key="drawing-histories-tab"
              tabPanelId="drawing-histories"
            >
              <ListView id="drawing-history-entries">
                {historyEntries?.map((history) => {
                  return (
                    <ListViewItem
                      key={history.id}
                      control="default"
                      title={history.title}
                      description={
                        <div>
                          <Text>{history.body}</Text>
                          <Text textStyle="Body.S">{history.note}</Text>
                        </div>
                      }
                      addOn={
                        <HistoryCreatedInfo
                          createdBy={history.createdBy}
                          createdAt={history.createdAt}
                        />
                      }
                    />
                  );
                })}
              </ListView>
            </TabPanel>,
            <TabPanel key="drawing-details-tab" tabPanelId="drawing-details">
              <PaddedContainer>
                <DrawingDetails drawing={drawing} />
              </PaddedContainer>
            </TabPanel>,
            <TabPanel key="drawing-items-tab" tabPanelId="drawing-items">
              <PaddedContainer>
                <DrawingArticles drawing={drawing} />
              </PaddedContainer>
            </TabPanel>,
          ]}
        />
      )}

      {step === "MORE_ACTIONS" && (
        <ElementsContainer>
          {drawing.viewUrl && (
            <>
              <Button text={t("label.view")} href={drawing.viewUrl} newWindow />
              <Button
                text={t("action.copy_link")}
                onClick={() =>
                  handleDrawingLinkCopy(drawing.viewUrl!, updateToast)
                }
              />
            </>
          )}
          {!disableAction && (
            <Button
              text={t("action.remove_from_project")}
              onClick={() => setStep("CONFIRM")}
            />
          )}
        </ElementsContainer>
      )}

      {step === "CONFIRM" && (
        <ElementsContainer>
          <Text>{t("prompt.confirm_remove_drawing")}</Text>
          <Button
            text={t("label.yes")}
            type="primary"
            onClick={() => {
              onDisassociate?.();
              closeModal();
            }}
          />
          <Button text={t("label.no")} onClick={() => setStep("MANAGE")} />
        </ElementsContainer>
      )}
    </ModalSheet>
  );
};
